import JsSIP from 'jssip'
import {
  getMySoftphoneRegistration,
  setAgentSoftphoneExtension,
} from '@/api/softphoneApi'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { parseAwsJson } from '@/utils/graphql'

/**
 * Global softphone service — one persistent SIP/WebRTC registration per
 * browser session. Supports:
 *  1. Direct dial-out via JsSIP (callNumber)
 *  2. Server-triggered calls via initiateSoftphoneCall (auto-answered here)
 *
 * Critical: remote audio is attached by patching RTCPeerConnection at
 * construction time (§5 of the softphone guide) — attaching to the
 * session's 'peerconnection' event races and silently drops audio.
 *
 * Also critical: the remote <audio> must NOT use the HTML `hidden` attribute
 * (display:none) — many browsers refuse to play MediaStream audio then.
 */

const REMOTE_AUDIO_ELEMENT_ID = 'softphone-remote-audio'
const SIP_DOMAIN = 'asterisk.yandle.io'
const REGISTER_TIMEOUT_MS = 20000
const PROVISION_SETTLE_MS = 1500

/** STUN + TURN — both required for NAT / mobile networks. Do not remove TURN. */
export const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    {
      urls: 'turn:asterisk.yandle.io:3478',
      username: 'softphoneturn',
      credential: 'Y4ndl3TurnPass2026',
    },
  ],
}

let ua = null
let starting = null
let activeSession = null
let peerConnectionPatched = false
let NativeRTCPeerConnection = null
/** ICE config from last successful credential fetch (TURN from API). */
let sessionIceServers = ICE_SERVERS

/** Call UI state */
let callPhase = 'idle' // idle | ringing | connected
let callStartedAt = null
let remoteMediaStream = null
let ringbackCtx = null
let ringbackTimer = null
let ringbackOscillators = []
/** Mic stream acquired on user gesture for server-originated auto-answer. */
let pendingLocalStream = null
/** True after initiateSoftphoneCall until SIP INVITE arrives or times out. */
let expectingServerCall = false
let expectingServerCallTimer = null
let onExpectServerCallTimeout = null

/**
 * A genuinely unexpected incoming session -- e.g. a customer call redirected
 * to this agent via an on-call action -- waiting for the agent to manually
 * accept or decline. Distinct from the auto-answered server-originated case
 * above: `expectingServerCall` is only ever armed right before this UA's own
 * outbound-triggered call comes back in, so its absence on an incoming
 * session reliably means "someone else is calling this agent right now."
 */
let pendingIncomingSession = null
let pendingIncomingIceServers = null
let pendingIncomingCallerId = null
let incomingRingCtx = null
let incomingRingTimer = null
let incomingRingOscillators = []

function getRemoteAudioElement() {
  return document.getElementById(REMOTE_AUDIO_ELEMENT_ID)
}

function ensureRemoteAudioElement() {
  let el = getRemoteAudioElement()
  if (el) return el
  if (typeof document === 'undefined') return null

  // Fallback if LandingPage audio node was removed — keep it audible to the
  // audio graph (never display:none / hidden).
  el = document.createElement('audio')
  el.id = REMOTE_AUDIO_ELEMENT_ID
  el.autoplay = true
  el.setAttribute('playsinline', 'true')
  el.setAttribute('webkit-playsinline', 'true')
  el.className = 'softphone-remote-audio'
  document.body.appendChild(el)
  return el
}

function attachStreamToRemoteAudio(stream) {
  if (!stream) return
  const remoteAudio = ensureRemoteAudioElement()
  if (!remoteAudio) return

  // The synthetic local ringback (startRingback) is only meant to fill the
  // silence when there's no real early media yet -- see its own comment.
  // But nothing was actually stopping it once real audio DID arrive (early
  // media on 'progress', or the carrier's own ringback/announcement RTP),
  // so the synthetic beep kept looping on top of the real audio until the
  // call was fully answered. Any real remote stream reaching this point
  // means genuine audio is about to play, so the synthetic tone is now
  // redundant -- stop it here, the one place every real-audio path
  // (track/addstream/peerconnection receivers) funnels through.
  stopRingback()

  remoteMediaStream = stream
  remoteAudio.muted = false
  remoteAudio.volume = 1
  remoteAudio.srcObject = stream

  const tryPlay = () => {
    const playPromise = remoteAudio.play()
    if (playPromise?.catch) {
      playPromise.catch((err) => {
        console.warn('Softphone remote audio play blocked', err?.message || err)
      })
    }
  }

  tryPlay()
  // Track may become live slightly after srcObject is set.
  stream.getAudioTracks().forEach((track) => {
    track.onunmute = tryPlay
  })
}

function attachReceiversFromPeerConnection(pc) {
  if (!pc) return
  const stream = new MediaStream()
  const receivers = pc.getReceivers?.() || []
  receivers.forEach((receiver) => {
    if (receiver?.track && receiver.track.kind === 'audio') {
      stream.addTrack(receiver.track)
    }
  })
  if (stream.getAudioTracks().length) {
    attachStreamToRemoteAudio(stream)
  }
}

function clearRemoteAudio() {
  const remoteAudio = getRemoteAudioElement()
  if (remoteAudio) {
    try {
      remoteAudio.pause()
    } catch {
      /* ignore */
    }
    remoteAudio.srcObject = null
  }
  remoteMediaStream = null
}

function stopPendingLocalStream() {
  if (!pendingLocalStream) return
  pendingLocalStream.getTracks().forEach((track) => {
    try {
      track.stop()
    } catch {
      /* ignore */
    }
  })
  pendingLocalStream = null
}

function clearExpectServerCall() {
  expectingServerCall = false
  if (expectingServerCallTimer) {
    clearTimeout(expectingServerCallTimer)
    expectingServerCallTimer = null
  }
  onExpectServerCallTimeout = null
}

function withTimeout(promise, ms, label = 'Operation') {
  let timer = null
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    )
  })
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer)
  })
}

/**
 * Acquire mic (+ unlock remote audio) during a user gesture so the later
 * AMI-originated INVITE can auto-answer without a blocked getUserMedia.
 * Never hangs forever — times out so the call API can still run.
 */
export async function prepareSoftphoneMedia({ timeoutMs = 4000 } = {}) {
  ensureRemoteAudioElement()
  const remoteAudio = ensureRemoteAudioElement()
  if (remoteAudio) {
    remoteAudio.muted = false
    remoteAudio.volume = 1
    // Do not await — empty <audio> play() can hang or stall in some browsers.
    const playPromise = remoteAudio.play?.()
    if (playPromise?.catch) playPromise.catch(() => {})
  }

  const live =
    pendingLocalStream &&
    pendingLocalStream.getAudioTracks().some((track) => track.readyState === 'live')
  if (live) return pendingLocalStream

  if (!navigator?.mediaDevices?.getUserMedia) {
    throw new Error('Microphone is not available in this browser.')
  }

  try {
    const stream = await withTimeout(
      navigator.mediaDevices.getUserMedia({ audio: true, video: false }),
      timeoutMs,
      'Microphone access',
    )
    stopPendingLocalStream()
    pendingLocalStream = stream
    return pendingLocalStream
  } catch (error) {
    console.warn('Softphone media prepare failed', error?.message || error)
    throw error
  }
}

/**
 * After initiateSoftphoneCall SUCCESS — wait for the SIP INVITE to this UA.
 * onTimeout(message) fires if nothing arrives (registration / Asterisk issue).
 */
export function armExpectServerOriginatedCall(onTimeout, timeoutMs = 25000) {
  clearExpectServerCall()
  expectingServerCall = true
  // Local ringback while waiting for Asterisk to INVITE this UA.
  if (!activeSession) setCallPhase('ringing')
  onExpectServerCallTimeout = typeof onTimeout === 'function' ? onTimeout : null
  expectingServerCallTimer = setTimeout(() => {
    if (!expectingServerCall || activeSession) {
      clearExpectServerCall()
      return
    }
    const message =
      'Call was started on the server, but your softphone never received the ring. Check SIP registration / extension.'
    const cb = onExpectServerCallTimeout
    clearExpectServerCall()
    if (!activeSession) setCallPhase('idle')
    cb?.(message)
  }, timeoutMs)
}

function answerIncomingServerCall(session, iceServers) {
  clearExpectServerCall()
  bindSessionLifecycle(session)
  setCallPhase('ringing')

  const options = {
    mediaConstraints: { audio: true, video: false },
    pcConfig: iceServers || sessionIceServers,
  }
  if (
    pendingLocalStream &&
    pendingLocalStream.getAudioTracks().some((track) => track.readyState === 'live')
  ) {
    options.mediaStream = pendingLocalStream
  }

  try {
    console.info('Softphone auto-answering server-originated call')
    session.answer(options)
  } catch (error) {
    console.error('Softphone auto-answer failed', error)
    if (activeSession === session) activeSession = null
    setCallPhase('idle')
  }
}

function extractCallerId(session) {
  try {
    const fromUri = session?.remote_identity?.uri
    if (fromUri?.user) return fromUri.user
    const reqFrom = session?.request?.from?.uri
    if (reqFrom?.user) return reqFrom.user
  } catch {
    /* ignore */
  }
  return null
}

function stopIncomingRingtone() {
  if (incomingRingTimer) {
    clearInterval(incomingRingTimer)
    incomingRingTimer = null
  }
  incomingRingOscillators.forEach((osc) => {
    try {
      osc.stop()
    } catch {
      /* ignore */
    }
  })
  incomingRingOscillators = []
  if (incomingRingCtx) {
    try {
      incomingRingCtx.close()
    } catch {
      /* ignore */
    }
    incomingRingCtx = null
  }
}

/** Classic telephone ring cadence (~1s on, ~3s off) for a genuine incoming call. */
function startIncomingRingtone() {
  stopIncomingRingtone()
  if (
    typeof window === 'undefined' ||
    (!window.AudioContext && !window.webkitAudioContext)
  ) {
    return
  }

  const AudioCtx = window.AudioContext || window.webkitAudioContext
  try {
    incomingRingCtx = new AudioCtx()
  } catch {
    return
  }

  const playBurst = () => {
    if (!incomingRingCtx || incomingRingCtx.state === 'closed') return
    if (incomingRingCtx.state === 'suspended') {
      incomingRingCtx.resume().catch(() => {})
    }

    const now = incomingRingCtx.currentTime
    const gain = incomingRingCtx.createGain()
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.1, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0)
    gain.connect(incomingRingCtx.destination)

    ;[400, 450].forEach((freq) => {
      const osc = incomingRingCtx.createOscillator()
      osc.type = 'sine'
      osc.frequency.value = freq
      osc.connect(gain)
      osc.start(now)
      osc.stop(now + 1.0)
      incomingRingOscillators.push(osc)
    })
  }

  playBurst()
  incomingRingTimer = setInterval(playBurst, 4000)
}

function clearPendingIncomingCall() {
  pendingIncomingSession = null
  pendingIncomingIceServers = null
  pendingIncomingCallerId = null
  stopIncomingRingtone()
}

function handleGenuineIncomingCall(session, iceServers) {
  pendingIncomingSession = session
  pendingIncomingIceServers = iceServers || sessionIceServers
  pendingIncomingCallerId = extractCallerId(session)
  console.info('Softphone: genuine incoming call, ringing for manual answer', pendingIncomingCallerId)
  startIncomingRingtone()

  session.on('failed', (failure) => {
    if (pendingIncomingSession === session) {
      console.warn('Softphone incoming call failed before answer', failure?.cause)
      clearPendingIncomingCall()
    }
  })
  session.on('ended', () => {
    if (pendingIncomingSession === session) clearPendingIncomingCall()
  })
}

/** Agent accepts a ringing genuine incoming call from the popup. */
export function answerIncomingCall() {
  if (!pendingIncomingSession) return false
  const session = pendingIncomingSession
  const iceServers = pendingIncomingIceServers
  clearPendingIncomingCall()

  bindSessionLifecycle(session)
  const options = {
    mediaConstraints: { audio: true, video: false },
    pcConfig: iceServers || sessionIceServers,
  }
  if (
    pendingLocalStream &&
    pendingLocalStream.getAudioTracks().some((track) => track.readyState === 'live')
  ) {
    options.mediaStream = pendingLocalStream
  }

  try {
    session.answer(options)
    return true
  } catch (error) {
    console.error('Softphone manual answer failed', error)
    if (activeSession === session) activeSession = null
    setCallPhase('idle')
    return false
  }
}

/** Agent declines a ringing genuine incoming call from the popup. */
export function declineIncomingCall() {
  if (!pendingIncomingSession) return false
  const session = pendingIncomingSession
  clearPendingIncomingCall()
  try {
    session.terminate()
    return true
  } catch (error) {
    console.warn('Softphone decline failed', error)
    return false
  }
}

function stopRingback() {
  if (ringbackTimer) {
    clearInterval(ringbackTimer)
    ringbackTimer = null
  }
  ringbackOscillators.forEach((osc) => {
    try {
      osc.stop()
    } catch {
      /* ignore */
    }
  })
  ringbackOscillators = []
  if (ringbackCtx) {
    try {
      ringbackCtx.close()
    } catch {
      /* ignore */
    }
    ringbackCtx = null
  }
}

/**
 * Was a synthetic dual-tone (US ringback pattern) filling the silence while
 * an outbound call had no early-media SDP yet. Removed 2026-08-14 -- it
 * played on its own timer regardless of whatever the carrier was actually
 * sending, so a call that DID have early media/progress tones got the real
 * audio and this synthetic one layered on top (stopRingback() on first real
 * audio in attachStreamToRemoteAudio only closes that gap, it doesn't
 * prevent the overlap already having played). Reported as a confusing
 * "extra sound" while a call is ringing. setCallPhase still calls this on
 * every ringing/connected transition -- kept as a no-op rather than
 * removing those call sites, so the call-phase state machine and its
 * effects on hold/mute etc. are untouched; the caller now just hears
 * silence (or the carrier's own real ringback, if it sends one) until the
 * call connects.
 */
function startRingback() {
  stopRingback()
}

function setCallPhase(phase) {
  callPhase = phase
  if (phase === 'connected') {
    callStartedAt = callStartedAt || Date.now()
    stopRingback()
  } else if (phase === 'ringing') {
    callStartedAt = null
    startRingback()
  } else {
    callStartedAt = null
    stopRingback()
  }
}

function wirePeerConnectionAudio(pc) {
  if (!pc || pc.__softphoneAudioWired) return
  pc.__softphoneAudioWired = true

  const remoteStream = new MediaStream()

  pc.addEventListener('track', (trackEvent) => {
    const track = trackEvent.track
    if (!track) return

    if (trackEvent.streams?.[0]) {
      attachStreamToRemoteAudio(trackEvent.streams[0])
      return
    }

    if (!remoteStream.getTracks().includes(track)) {
      remoteStream.addTrack(track)
    }
    attachStreamToRemoteAudio(remoteStream)
  })

  // Some stacks fire addstream instead of (or before) track.
  pc.addEventListener('addstream', (event) => {
    if (event?.stream) attachStreamToRemoteAudio(event.stream)
  })
}

/**
 * Patch RTCPeerConnection so the remote track listener is attached at the
 * moment of construction. JsSIP fires 'peerconnection' synchronously before
 * ua.call()/session.answer() return — listening on the session races and
 * silently drops remote audio even when RTP is flowing.
 */
function ensurePeerConnectionPatch() {
  if (peerConnectionPatched || typeof window === 'undefined') return
  if (!window.RTCPeerConnection) return

  NativeRTCPeerConnection = window.RTCPeerConnection

  window.RTCPeerConnection = class SoftphoneRTCPeerConnection extends (
    NativeRTCPeerConnection
  ) {
    constructor(...args) {
      super(...args)
      wirePeerConnectionAudio(this)
    }
  }

  peerConnectionPatched = true
}

function restorePeerConnection() {
  if (!peerConnectionPatched || !NativeRTCPeerConnection) return
  window.RTCPeerConnection = NativeRTCPeerConnection
  peerConnectionPatched = false
  NativeRTCPeerConnection = null
}

export function isSoftphoneRegistered() {
  return Boolean(ua?.isRegistered?.())
}

export function getActiveSoftphoneSession() {
  return activeSession
}

function bindSessionLifecycle(session) {
  activeSession = session
  // Show ringing until accepted (outbound progress / inbound auto-answer).
  setCallPhase('ringing')

  // Belt-and-suspenders: also wire when JsSIP exposes the PC on the session.
  session.on('peerconnection', (data) => {
    const pc = data?.peerconnection || session.connection
    wirePeerConnectionAudio(pc)
    attachReceiversFromPeerConnection(pc)
  })

  session.on('progress', () => {
    setCallPhase('ringing')
    attachReceiversFromPeerConnection(session.connection)
  })

  session.on('accepted', () => {
    setCallPhase('connected')
    attachReceiversFromPeerConnection(session.connection)
    // Re-assert playback after answer (autoplay policies + late tracks).
    if (remoteMediaStream) attachStreamToRemoteAudio(remoteMediaStream)
  })

  session.on('confirmed', () => {
    setCallPhase('connected')
    attachReceiversFromPeerConnection(session.connection)
    if (remoteMediaStream) attachStreamToRemoteAudio(remoteMediaStream)
  })

  const clearCall = (label, detail) => {
    if (activeSession === session) activeSession = null
    setCallPhase('idle')
    clearRemoteAudio()
    if (label === 'ended') console.info('Softphone call ended')
    else console.warn('Softphone call failed', detail)
  }

  session.on('ended', () => clearCall('ended'))
  session.on('failed', (failure) => clearCall('failed', failure?.cause))
}

function normalizeDialNumber(number) {
  return String(number ?? '')
    .trim()
    .replace(/[\s()-]/g, '')
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Eligible: AGENT + HUMAN_AGENT, or OWNER / ADMIN.
 * AI agents are never softphone-eligible.
 */
export function isSoftphoneEligible({ userRole, agentType } = {}) {
  const role = String(userRole ?? '').trim().toUpperCase()
  if (role === 'OWNER' || role === 'ADMIN') return true
  if (role !== 'AGENT') return false
  const type = String(agentType ?? '').trim().toUpperCase()
  return type !== 'AI_AGENT'
}

/**
 * Normalize getMySoftphoneRegistration envelopes:
 *  - { status, data: { extension, sip_password, websocket_uri } }
 *  - flat { extension, sip_password, websocket_uri }
 *  - data / items double-encoded as AWSJSON strings
 */
function extractSoftphoneCredentials(response) {
  if (response == null) return null

  const roots = [response]
  const parsedData = parseAwsJson(response?.data)
  if (parsedData != null) roots.push(parsedData)
  if (response?.data != null && response.data !== parsedData) {
    roots.push(response.data)
  }

  for (const root of roots) {
    if (root == null || typeof root !== 'object') continue

    const nodes = [root]
    const items = root.items ?? root.Items
    if (Array.isArray(items) && items[0]) nodes.push(parseAwsJson(items[0]) ?? items[0])
    else if (items && typeof items === 'object') {
      nodes.push(parseAwsJson(items) ?? items)
    }

    for (const node of nodes) {
      if (node == null || typeof node !== 'object') continue
      const extension = String(node.extension ?? node.Extension ?? '').trim()
      const sipPassword = String(
        node.sip_password ?? node.sipPassword ?? node.password ?? '',
      ).trim()
      const websocketUri = String(
        node.websocket_uri ?? node.websocketUri ?? node.ws_uri ?? '',
      ).trim()

      if (extension && sipPassword && websocketUri) {
        const turnUrls = node.turn_urls ?? node.turnUrls
        const turnUsername = String(
          node.turn_username ?? node.turnUsername ?? '',
        ).trim()
        const turnCredential = String(
          node.turn_credential ?? node.turnCredential ?? '',
        ).trim()

        return {
          extension,
          sipPassword,
          websocketUri,
          turnUrls: Array.isArray(turnUrls) ? turnUrls.filter(Boolean) : [],
          turnUsername,
          turnCredential,
        }
      }
    }
  }

  return null
}

/** Prefer TURN from getMySoftphoneRegistration; fall back to guide defaults. */
function buildIceServers(credentials = {}) {
  const turnUrls =
    credentials.turnUrls?.length > 0
      ? credentials.turnUrls
      : ['turn:asterisk.yandle.io:3478']
  const turnUsername = credentials.turnUsername || 'softphoneturn'
  const turnCredential = credentials.turnCredential || 'Y4ndl3TurnPass2026'

  return {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      {
        urls: turnUrls.length === 1 ? turnUrls[0] : turnUrls,
        username: turnUsername,
        credential: turnCredential,
      },
    ],
  }
}

function registrationLooksAvailable(response) {
  if (extractSoftphoneCredentials(response)) return true
  return isApiSuccess(response)
}

async function fetchOrProvisionRegistration(currentUserId) {
  let response = await getMySoftphoneRegistration()
  if (registrationLooksAvailable(response)) return response

  const message = String(
    response?.message ?? response?.status_message ?? response?.statusMessage ?? '',
  )
  const needsProvision = /no softphone extension/i.test(message)
  const selfId = String(currentUserId ?? '').trim()

  if (!needsProvision || !selfId) return response

  // Self-service provision once, then refetch credentials.
  const provisioned = await setAgentSoftphoneExtension({ user_id: selfId })
  if (!isApiSuccess(provisioned) && !extractSoftphoneCredentials(provisioned)) {
    console.warn('Softphone self-provision failed:', provisioned?.message)
    return response
  }

  // Asterisk/PJSIP may lag the Dynamo write — brief settle before REGISTER.
  await sleep(PROVISION_SETTLE_MS)
  response = await getMySoftphoneRegistration()
  return response
}

function stopUaInstance(instance) {
  if (!instance) return
  try {
    instance.stop()
  } catch {
    /* ignore */
  }
}

/**
 * Start the UA and resolve only after SIP REGISTER succeeds (or fails).
 * Avoids keeping a zombie UA that reconnects forever with bad credentials.
 */
function startAndAwaitRegistration(instance) {
  return new Promise((resolve, reject) => {
    let settled = false

    const finish = (fn, value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      fn(value)
    }

    const onRegistered = () => {
      console.info('Softphone registered')
      finish(resolve, instance)
    }

    const onFailed = (event) => {
      const cause = event?.cause || 'Registration failed'
      const status = event?.response?.status_code
      const reason = event?.response?.reason_phrase
      console.error(
        'Softphone registration failed',
        cause,
        status ? `(SIP ${status} ${reason || ''})`.trim() : '',
      )
      stopUaInstance(instance)
      finish(
        reject,
        new Error(
          status ? `${cause} (SIP ${status}${reason ? ` ${reason}` : ''})` : String(cause),
        ),
      )
    }

    const timer = setTimeout(() => {
      stopUaInstance(instance)
      finish(reject, new Error('Softphone registration timed out'))
    }, REGISTER_TIMEOUT_MS)

    instance.on('registered', onRegistered)
    instance.on('registrationFailed', onFailed)

    instance.on('disconnected', () => {
      if (!settled) {
        console.warn('Softphone socket disconnected, reconnecting…')
      }
    })

    instance.start()
  })
}

async function createSession(currentUserId) {
  ensurePeerConnectionPatch()
  ensureRemoteAudioElement()

  const response = await fetchOrProvisionRegistration(currentUserId)
  const credentials = extractSoftphoneCredentials(response)

  if (!credentials) {
    console.warn(
      'Softphone registration unavailable:',
      response?.message ?? response?.status_message ?? 'incomplete credentials',
    )
    return null
  }

  const { extension, sipPassword, websocketUri } = credentials
  const iceServers = buildIceServers(credentials)
  sessionIceServers = iceServers

  // Guide §4 UA options exactly — SIP host is always asterisk.yandle.io.
  const socket = new JsSIP.WebSocketInterface(websocketUri)
  const instance = new JsSIP.UA({
    sockets: [socket],
    uri: `sip:${extension}@${SIP_DOMAIN}`,
    password: sipPassword,
    register: true,
  })

  // Server-triggered initiateSoftphoneCall arrives as an incoming session —
  // auto-answer with ICE/TURN so audio connects through NAT.
  instance.on('newRTCSession', (event) => {
    const session = event.session
    console.info(
      'Softphone newRTCSession',
      session?.direction,
      expectingServerCall ? '(expecting server call)' : '',
    )
    // Outbound sessions are bound in callNumber(); still bind here if missed.
    if (session.direction === 'incoming') {
      if (expectingServerCall) {
        answerIncomingServerCall(session, iceServers)
      } else {
        handleGenuineIncomingCall(session, iceServers)
      }
      return
    }

    if (!activeSession) bindSessionLifecycle(session)
  })

  // Mic permission is deliberately NOT requested here -- this runs right
  // after login (see maybeStartSoftphone), and prompting before the agent
  // has done anything reads as the app grabbing the mic unprompted. It's
  // requested on-demand instead, tied to the actual user gesture: see
  // prepareSoftphoneMedia, called from CampaignDetailPage's placeSipCall
  // right before a call goes out (the only place a call is originated --
  // SoftphoneDialer's FAB is in-call controls only, it never dials). That
  // already runs early enough that pendingLocalStream is warm by the time
  // the AMI-originated INVITE for that same call arrives and
  // answerIncomingServerCall reads it below.

  try {
    return await startAndAwaitRegistration(instance)
  } catch (error) {
    console.error(
      `Softphone SIP auth failed for extension ${extension}. ` +
        'AppSync credentials were loaded, but Asterisk rejected the digest — ' +
        'the sip_password in Dynamo is likely out of sync with PJSIP auth. ' +
        'Ask backend to re-sync/re-provision extension ' +
        `${extension} on asterisk.yandle.io.`,
      error?.message || error,
    )
    return null
  }
}

/**
 * Start (or reuse) the session-wide SIP registration.
 * Pass currentUserId so a missing extension can self-provision once.
 */
export async function startSoftphoneSession(currentUserId) {
  if (ua?.isRegistered?.()) return ua
  if (ua) {
    // Stale / failed UA from a prior attempt — tear down before retrying.
    stopSoftphoneSession()
  }
  if (starting) return starting

  starting = createSession(currentUserId)
    .then((instance) => {
      ua = instance
      return instance
    })
    .catch((error) => {
      console.warn('Softphone session failed to start', error)
      return null
    })
    .finally(() => {
      starting = null
    })

  return starting
}

/** Tear down the registration. Call on logout / app unmount. */
export function stopSoftphoneSession() {
  clearExpectServerCall()
  if (pendingIncomingSession) {
    try {
      pendingIncomingSession.terminate()
    } catch {
      /* ignore */
    }
    clearPendingIncomingCall()
  }
  if (activeSession) {
    try {
      activeSession.terminate()
    } catch {
      /* ignore */
    }
    activeSession = null
  }

  setCallPhase('idle')
  clearRemoteAudio()
  stopPendingLocalStream()

  if (ua) {
    try {
      ua.stop()
    } catch (error) {
      console.warn('Softphone session failed to stop cleanly', error)
    }
    ua = null
  }

  restorePeerConnection()
}

/**
 * Direct browser dial-out (JsSIP). Destination may include a leading +;
 * Asterisk normalizes. Prefer E.164 with country code.
 */
export function callNumber(number) {
  if (!ua?.isRegistered?.()) {
    throw new Error('Softphone is not registered yet.')
  }

  const dial = normalizeDialNumber(number)
  if (!dial) {
    throw new Error('Enter a phone number to call.')
  }

  ensurePeerConnectionPatch()
  ensureRemoteAudioElement()

  // Prefer the configured SIP domain for the dial URI.
  const target = `sip:${dial.replace(/^\+/, '')}@${SIP_DOMAIN}`

  const session = ua.call(target, {
    mediaConstraints: { audio: true, video: false },
    pcConfig: sessionIceServers,
    rtcOfferConstraints: {
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    },
  })

  bindSessionLifecycle(session)

  // Immediate attach if PC already exists (JsSIP may create it synchronously).
  attachReceiversFromPeerConnection(session.connection)

  return session
}

/** Hang up the active softphone call, if any. */
export function hangupSoftphoneCall() {
  clearExpectServerCall()
  if (!activeSession) {
    setCallPhase('idle')
    return false
  }
  try {
    activeSession.terminate()
  } catch (error) {
    console.warn('Softphone hangup failed', error)
    return false
  }
  return true
}

/** Snapshot for dialer UI. */
export function getSoftphoneStatus() {
  const inCall = Boolean(activeSession && !activeSession.isEnded?.())
  const phase = inCall ? callPhase : expectingServerCall ? 'ringing' : 'idle'
  const muted = Boolean(inCall && activeSession?.isMuted?.()?.audio)
  return {
    registered: Boolean(ua?.isRegistered?.()),
    starting: Boolean(starting),
    inCall: inCall || expectingServerCall,
    expectingServerCall,
    phase,
    muted,
    direction: inCall ? activeSession?.direction || null : null,
    incomingCall: pendingIncomingSession
      ? { callerId: pendingIncomingCallerId }
      : null,
    callStartedAt: phase === 'connected' ? callStartedAt : null,
    durationSec:
      phase === 'connected' && callStartedAt
        ? Math.max(0, Math.floor((Date.now() - callStartedAt) / 1000))
        : 0,
  }
}

/** Mute / unmute local mic on the active call. */
export function setSoftphoneMuted(muted) {
  if (!activeSession || activeSession.isEnded?.()) return false
  try {
    if (muted) activeSession.mute({ audio: true, video: false })
    else activeSession.unmute({ audio: true, video: false })
    return true
  } catch (error) {
    console.warn('Softphone mute failed', error)
    return false
  }
}

/** Send DTMF on the active call (keypad during connected call). */
export function sendSoftphoneDtmf(tone) {
  const digit = String(tone ?? '').trim()
  if (!digit || !activeSession || activeSession.isEnded?.()) return false
  try {
    activeSession.sendDTMF(digit)
    return true
  } catch (error) {
    console.warn('Softphone DTMF failed', error)
    return false
  }
}
